import {
  MasterpassAccountAccessRequest,
  MasterpassAccountAccessResult,
  MasterpassAddCardRequest,
  MasterpassCallback,
  MasterpassCompleteRegistrationRequest,
  MasterpassDirectPaymentRequest,
  MasterpassLinkToMerchantRequest,
  MasterpassPaymentRequest,
  MasterpassRecurringOrderDeleteRequest,
  MasterpassRecurringOrderRequest,
  MasterpassRecurringOrderUpdateRequest,
  MasterpassRegisterAndPurchaseRequest,
  MasterpassRemoveCardRequest,
  MasterpassResendOtpRequest,
  MasterpassResponse,
  MasterpassVerifyOtpRequest,
} from 'Models/MasterpassSdkModels';
import { isMasterpassSdkLoaded } from './MasterpassBootstrap';

/**
 * Masterpass Javascript SDK Web v1.0.7 servis katmanı.
 *
 * Eski `window.MFS` tabanlı katmanın yerini alır. İki temel fark:
 *
 *  1. Artık jQuery ile gizli `<form>` üretilmiyor — SDK düz JSON alıyor.
 *     (`MasterPassHtlmHelper` bu yüzden kaldırıldı.)
 *  2. Kart numarası içeren metodlar geçersiz PAN'da SENKRON exception fırlatıyor.
 *     Burada yakalanıp callback'e hata cevabı olarak veriliyor, böylece çağıran
 *     katmanın tek bir hata yolu oluyor.
 *
 * Cevap okuma bu dosyanın işi değildir — `Utils/MasterpassResponse.ts` içindeki
 * `getMasterpassResponseCode()` / `isMasterpassSuccess()` kullanılmalıdır.
 */

/** SDK yüklü değilken ya da senkron exception'da üretilen yapay hata cevabı. */
const buildLocalErrorResponse = (message: string): MasterpassResponse<any> => ({
  statusCode: 0,
  responseCode: 'SDK_ERROR',
  description: message,
  result: {},
  exception: { code: 'SDK_ERROR', message },
});

/**
 * SDK çağrılarını sarmalar: SDK yüklü değilse ya da çağrı senkron exception
 * fırlatırsa (geçersiz kart numarası) callback'i hata cevabıyla tetikler.
 */
const guard = <TResult>(callback: MasterpassCallback<TResult>, invoke: () => void): void => {
  if (!isMasterpassSdkLoaded()) {
    callback(0, buildLocalErrorResponse('Masterpass SDK yüklenemedi.') as MasterpassResponse<TResult>);
    return;
  }

  try {
    invoke();
  } catch (error: any) {
    // SDK `addCard` / `directPayment` / `registerAndPurchase` içinde Luhn kontrolü
    // yapıp geçersiz PAN'da `throw new Error('Kart numarası geçerli değil!')` atıyor.
    callback(
      0,
      buildLocalErrorResponse(error?.message ?? 'Masterpass işlemi başlatılamadı.') as MasterpassResponse<TResult>
    );
  }
};

const MasterPassServices = {
  /* ---------------------------------------------------------------- *
   * Hesap ve kart işlemleri
   * ---------------------------------------------------------------- */

  /** Kayıtlı kartları listeler. Eski karşılığı: `MFS.listCards`. */
  accountAccess: (
    request: MasterpassAccountAccessRequest,
    callback: MasterpassCallback<MasterpassAccountAccessResult>
  ) => guard(callback, () => window.Masterpass.accountService.accountAccess(request, callback)),

  /**
   * Masterpass hesabına kart ekler. Eski karşılığı: `MFS.register`.
   * PAN ve CVV düz verilir; SDK Luhn kontrolü yapıp RSA ile şifreler.
   */
  addCard: (request: MasterpassAddCardRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.accountService.addCard(request, callback)),

  /**
   * Kartı siler. Eski karşılığı: `MFS.deleteCard`.
   * ⚠️ Kart artık maskeli PAN ile değil, `cardAlias` (kart adı) ile hedeflenir.
   */
  removeCard: (request: MasterpassRemoveCardRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.accountService.removeCard(request, callback)),

  /** Masterpass hesabını üye işyerine bağlar. Eski karşılığı: `MFS.linkCardToClient`. */
  linkToMerchant: (request: MasterpassLinkToMerchantRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.accountService.linkToMerchant(request, callback)),

  /**
   * Ödeme sonrası kart kaydını tamamlar (3D/OTP adımının ardından gelen
   * "kartı kaydet" ekranı). Eski SDK'da karşılığı yoktu.
   *
   * `token` verilmezse SDK akıştaki `FlowDirectable.token`'ı kullanır.
   */
  completeRegistration: (
    request: MasterpassCompleteRegistrationRequest,
    callback: MasterpassCallback,
    token: string | null = null
  ) => guard(callback, () => window.Masterpass.accountService.completeRegistration(request, token, callback)),

  /* ---------------------------------------------------------------- *
   * Ödeme
   * ---------------------------------------------------------------- */

  /**
   * Kayıtlı kartla ödeme. Eski karşılığı: `MFS.purchase`.
   * ⚠️ Kart artık `rtaPan` (maskeli PAN) ile değil `cardAlias` ile seçilir.
   */
  payment: (request: MasterpassPaymentRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.paymentService.payment(request, callback)),

  /** Kart kaydetmeden tek seferlik ödeme. Eski karşılığı: `MFS.directPurchase`. */
  directPayment: (request: MasterpassDirectPaymentRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.paymentService.directPayment(request, callback)),

  /** Kartı kaydedip aynı işlemde ödeme alır. Eski SDK'da karşılığı yoktu. */
  registerAndPurchase: (request: MasterpassRegisterAndPurchaseRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.paymentService.registerAndPurchase(request, callback)),

  /* ---------------------------------------------------------------- *
   * Doğrulama
   * ---------------------------------------------------------------- */

  /**
   * OTP / CVV / RTA doğrulaması. Eski karşılığı: `MFS.validateTransaction`.
   *
   * ⚠️ Hedef adres SDK'nın bir önceki 202 cevabından tuttuğu `FlowDirectable.uri`'dir.
   * Araya başka bir Masterpass çağrısı girerse bu adres ezilir ve doğrulama
   * yanlış işleme gider — doğrulama, kendisini tetikleyen istekten hemen sonra
   * çağrılmalıdır.
   */
  verifyOtp: (request: MasterpassVerifyOtpRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.verifyService.verifyOtp(request, callback)),

  /**
   * OTP'yi yeniden gönderir. Eski karşılığı: `MFS.resendOtp(token, dil, cb)`.
   * ⚠️ Token ve dil artık parametre değil: token'ı SDK akıştan alır, dil ise
   * `Masterpass.setLanguage()` ile global olarak kurulur.
   */
  resendOtp: (callback: MasterpassCallback, request: MasterpassResendOtpRequest = {}) =>
    guard(callback, () => window.Masterpass.verifyService.resendOtp(request, callback)),

  /* ---------------------------------------------------------------- *
   * Otomatik talimat (recurring)
   * ---------------------------------------------------------------- */

  /** Eski karşılığı: `MFS.initiateRecurringPayment` (`actionType: 'A'`). */
  recurringOrder: (request: MasterpassRecurringOrderRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.accountService.recurringOrder(request, callback)),

  /** Talimat günceller. Eski SDK'da ayrı metot yoktu, `actionType` ile yapılıyordu. */
  recurringOrderUpdate: (request: MasterpassRecurringOrderUpdateRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.accountService.recurringOrderUpdate(request, callback)),

  /** Eski karşılığı: `MFS.initiateRecurringPayment` (`actionType: 'D'`). */
  recurringOrderDelete: (request: MasterpassRecurringOrderDeleteRequest, callback: MasterpassCallback) =>
    guard(callback, () => window.Masterpass.accountService.recurringOrderDelete(request, callback)),
};

export default MasterPassServices;
